import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import Switch from '../../Switch.jsx';
import LoadingSpinner from '../LoadingSpinner.jsx';
import { useT } from '../../../contexts/LocaleContext.jsx';
import {
  parseGenres, isNotInCatalogue, closestOf, songLookupErrorMessage,
} from '../../../_helpers/manualSongs.js';
import '../../../styles/partial/modal/EditSongModal.css';

const songKeyOf = (song) => (song ? String(song.id ?? song.ID ?? '') : null);

/**
 * Why a save was refused, in the band's words. The edit route answers
 * text/plain, so the status is what tells these apart; the server's text is
 * never shown.
 */
const saveErrorMessage = (error, t) => {
  switch (error?.status) {
    case 409:
      return t('songList.editModal.errors.duplicate');
    case 404:
      return t('songList.editModal.errors.notOnList');
    case 400:
    case 422:
      return t('songList.editModal.errors.invalid');
    default:
      return t('songList.editModal.errors.saveFailed');
  }
};

/** Why "This isn't the right song" or "Find this song" failed (ENG-796). */
const conversionErrorMessage = (error, t) => {
  switch (error?.code) {
    case 'song_already_on_list':
      return t('songList.editModal.errors.alreadyOnList');
    case 'song_not_in_band_list':
      return t('songList.editModal.errors.notOnList');
    case 'song_not_manual':
      return t('songList.editModal.errors.alreadyMatched');
    default:
      break;
  }
  if (error?.status === 404) return t('songList.editModal.errors.notOnList');
  return songLookupErrorMessage(error, t) || t('songList.editModal.errors.saveFailed');
};

/**
 * Edits a song on the band's list: the band's own fields (lyrics, chords,
 * tempo, key, public) for any song, and for a song the band added anyway
 * (`is_manual`, ENG-795/796) every song-info field as well, including the
 * title and artist that are otherwise the catalogue's. Nothing labels such a
 * song: it simply has more to edit.
 *
 * A catalogue song has "This isn't the right song", which after a
 * confirmation makes it the band's own copy (`onConvertToManual`, resolving
 * once the list has the copy's new id). A song added anyway has "Find this
 * song", which looks it up again (`onFindMatch`) and, once the band confirms
 * the match, replaces it with the catalogue song (`onConfirmMatch`).
 *
 * `onSave` receives the request body. Every callback may reject with the
 * fetchWrapper error; the modal stays open and says why.
 */
const EditSongModal = ({
  song, isOpen, onClose, onSave, onConvertToManual, onFindMatch, onConfirmMatch,
}) => {
  const t = useT();
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [lyrics, setLyrics] = useState('');
  const [chords, setChords] = useState('');
  const [tempo, setTempo] = useState('');
  const [key, setKey] = useState('');
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Song info, editable on a song added anyway.
  const [album, setAlbum] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [songWriter, setSongWriter] = useState('');
  const [genres, setGenres] = useState('');
  const [explicit, setExplicit] = useState(false);
  // "This isn't the right song" (ENG-796).
  const [confirmingNotRight, setConfirmingNotRight] = useState(false);
  // "Find this song" (ENG-796): the lookup's answer, as find-match gave it.
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  // Genres and explicit come only with the single-song read, which can land
  // after the modal opened: what the performer already touched is kept.
  const touched = useRef(new Set());
  const initializedFor = useRef(null);

  const editsSongInfo = song?.is_manual === true;

  useEffect(() => {
    if (!isOpen || !song) {
      initializedFor.current = null;
      return;
    }
    const songKey = songKeyOf(song);
    if (initializedFor.current !== songKey) {
      initializedFor.current = songKey;
      touched.current = new Set();
      setTitle(song.title || '');
      setArtist(song.artist || '');
      setLyrics(song.lyrics || '');
      setChords(song.chords || '');
      setTempo(song.tempo || '');
      setKey(song.key || '');
      setActive(song.active !== undefined ? song.active : false);
      setAlbum(song.original_album || '');
      setReleaseDate(song.release_date || '');
      setSongWriter(song.song_writer || '');
      setGenres(Array.isArray(song.genres) ? song.genres.join(', ') : '');
      setExplicit(song.explicit === true);
      setConfirmingNotRight(false);
      setFound(null);
      setBusy(false);
      setError('');
      return;
    }
    // The same song again, with the details the single-song read added.
    if (!touched.current.has('genres') && Array.isArray(song.genres)) setGenres(song.genres.join(', '));
    if (!touched.current.has('explicit') && typeof song.explicit === 'boolean') setExplicit(song.explicit);
  }, [song, isOpen]);

  const touch = (field, setter) => (value) => {
    touched.current.add(field);
    setter(value);
  };

  const buildPayload = () => {
    const payload = { ...song, title, artist, lyrics, chords, tempo, key, active };
    if (!editsSongInfo) return payload;
    payload.title = title.trim();
    payload.artist = artist.trim();
    payload.original_album = album.trim();
    // '' clears the song's date on the server.
    payload.release_date = releaseDate;
    payload.song_writer = songWriter.trim();
    // Sent only when known: the list read carries no genres, and an empty
    // list here would wipe the song's.
    if (touched.current.has('genres') || Array.isArray(song?.genres)) payload.genres = parseGenres(genres);
    else delete payload.genres;
    if (touched.current.has('explicit') || typeof song?.explicit === 'boolean') payload.explicit = explicit;
    else delete payload.explicit;
    return payload;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    if (editsSongInfo && !title.trim()) {
      setError(t('addSong.errors.titleRequired'));
      return;
    }
    setLoading(true);
    try {
      await onSave(buildPayload());
    } catch (err) {
      setError(saveErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  };

  // "This isn't the right song": the list switches to the copy's new id and
  // hands it back as `song`, which then opens with its song info editable.
  const convertToManual = async () => {
    if (busy || !onConvertToManual) return;
    setError('');
    setBusy(true);
    try {
      await onConvertToManual(song);
      setConfirmingNotRight(false);
    } catch (err) {
      setError(conversionErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  // "Find this song", under the title and artist as they now stand in the
  // form (the band may correct them first), or a "Did you mean" candidate's.
  const findMatch = async (candidate = null) => {
    if (busy || !onFindMatch) return;
    const query = candidate ?? { title: title.trim(), artist: artist.trim() };
    if (candidate) {
      setTitle(candidate.title);
      setArtist(candidate.artist);
    }
    setError('');
    setFound(null);
    setBusy(true);
    try {
      const answer = await onFindMatch(song, query);
      setFound({
        matched: answer?.result === 'matched' && Boolean(answer.match),
        match: answer?.match ?? null,
        closest: closestOf(answer),
        alreadyOnList: answer?.already_on_list === true,
      });
    } catch (err) {
      setError(conversionErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  const confirmMatch = async () => {
    const match = found?.match;
    if (busy || !match || !onConfirmMatch) return;
    setError('');
    setBusy(true);
    try {
      await onConfirmMatch(song, { title: match.title, artist: match.artist });
    } catch (err) {
      if (isNotInCatalogue(err)) {
        setFound({ matched: false, match: null, closest: closestOf(err), alreadyOnList: false });
      } else {
        setError(conversionErrorMessage(err, t));
      }
    } finally {
      setBusy(false);
    }
  };

  const releaseYear = found?.match?.release_date ? String(found.match.release_date).slice(0, 4) : '';

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className="modalContent" overlayClassName="modalOverlay">
      <h2>{t('songList.editModal.title')}</h2>
      <form onSubmit={handleSave}>
        <div className="form-group">
          <label htmlFor="edit-song-title">{t('songList.editModal.titleLabel')}</label>
          <input id="edit-song-title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} readOnly={!editsSongInfo} />
        </div>
        <div className="form-group">
          <label htmlFor="edit-song-artist">{t('songList.editModal.artistLabel')}</label>
          <input id="edit-song-artist" type="text" value={artist} onChange={(e) => setArtist(e.target.value)} readOnly={!editsSongInfo} />
        </div>
        {editsSongInfo && (
          <>
            <div className="form-group">
              <button type="button" className="btn-link" onClick={() => findMatch()} disabled={busy} data-testid="edit-song-find">
                {t('songList.editModal.findSong')}
              </button>
            </div>
            {found && (
              <div className="song-match" role="status" data-testid="edit-song-match">
                {found.matched ? (
                  <>
                    <p>
                      {t('songList.editModal.found', { title: found.match.title, artist: found.match.artist })}
                      {(found.match.album || releaseYear) && (
                        <span className="song-match-detail">
                          {' '}
                          {found.match.album && releaseYear
                            ? t('songList.editModal.foundAlbumYear', { album: found.match.album, year: releaseYear })
                            : found.match.album
                              ? t('songList.editModal.foundAlbum', { album: found.match.album })
                              : t('songList.editModal.foundYear', { year: releaseYear })}
                        </span>
                      )}
                    </p>
                    {found.alreadyOnList ? (
                      <p data-testid="edit-song-already-on-list">{t('songList.editModal.errors.alreadyOnList')}</p>
                    ) : (
                      <div className="button-group">
                        <button type="button" className="btn-save" onClick={confirmMatch} disabled={busy} data-testid="edit-song-use-match">
                          {t('songList.editModal.useMatch')}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p>{t('songList.editModal.notFound')}</p>
                    {found.closest ? (
                      <button type="button" className="btn-link" onClick={() => findMatch(found.closest)} disabled={busy} data-testid="edit-song-did-you-mean">
                        {t('addSong.notFound.didYouMean', { title: found.closest.title, artist: found.closest.artist })}
                      </button>
                    ) : (
                      <p>{t('songList.editModal.checkSpelling')}</p>
                    )}
                  </>
                )}
              </div>
            )}
            <div className="form-group">
              <label htmlFor="edit-song-album">{t('songList.editModal.albumLabel')}</label>
              <input id="edit-song-album" type="text" maxLength={255} value={album} onChange={(e) => setAlbum(e.target.value)} />
            </div>
            <div className="form-group">
              <label htmlFor="edit-song-release-date">{t('songList.editModal.releaseDateLabel')}</label>
              <input id="edit-song-release-date" type="date" value={releaseDate} onChange={(e) => setReleaseDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label htmlFor="edit-song-genres">{t('songList.editModal.genresLabel')}</label>
              <input id="edit-song-genres" type="text" placeholder={t('songList.editModal.genresPlaceholder')} value={genres} onChange={(e) => touch('genres', setGenres)(e.target.value)} />
            </div>
            <div className="form-group">
              <label htmlFor="edit-song-writer">{t('songList.editModal.songWriterLabel')}</label>
              <input id="edit-song-writer" type="text" value={songWriter} onChange={(e) => setSongWriter(e.target.value)} />
            </div>
            <div className="form-group checkbox-group">
              <input id="edit-song-explicit" type="checkbox" checked={explicit} onChange={(e) => touch('explicit', setExplicit)(e.target.checked)} />
              <label htmlFor="edit-song-explicit">{t('songList.editModal.explicitLabel')}</label>
            </div>
          </>
        )}
        <div className="form-group">
          <label htmlFor="edit-song-lyrics">{t('songList.editModal.lyricsLabel')}</label>
          <textarea id="edit-song-lyrics" value={lyrics} onChange={(e) => setLyrics(e.target.value)} />
        </div>
        <div className="form-group">
          <label htmlFor="edit-song-chords">{t('songList.editModal.chordsLabel')}</label>
          <textarea id="edit-song-chords" value={chords} onChange={(e) => setChords(e.target.value)} />
        </div>
        <div className="form-group">
          <label htmlFor="edit-song-tempo">{t('songList.editModal.tempoLabel')}</label>
          <input id="edit-song-tempo" type="text" value={tempo} onChange={(e) => setTempo(e.target.value)} />
        </div>
        <div className="form-group">
          <label htmlFor="edit-song-key">{t('songList.editModal.keyLabel')}</label>
          <input id="edit-song-key" type="text" value={key} onChange={(e) => setKey(e.target.value)} />
        </div>
        <div className="form-group switch-group">
          <label>{t('songList.editModal.activeLabel')}</label>
          <Switch isOn={active} handleToggle={() => setActive(!active)} switchId='switchIsActive' />
        </div>
        {!editsSongInfo && !confirmingNotRight && (
          <div className="form-group">
            <button type="button" className="btn-link" onClick={() => setConfirmingNotRight(true)} data-testid="edit-song-not-right">
              {t('songList.editModal.notRightSong')}
            </button>
          </div>
        )}
        {!editsSongInfo && confirmingNotRight && (
          <div className="song-confirm" role="alert" data-testid="edit-song-confirm-not-right">
            <p>{t('songList.editModal.confirmNotRightSong')}</p>
            <div className="button-group">
              <button type="button" className="btn-save" onClick={convertToManual} disabled={busy} data-testid="edit-song-confirm-not-right-yes">
                {t('songList.editModal.confirmNotRightSongAction')}
              </button>
              <button type="button" className="btn-cancel" onClick={() => setConfirmingNotRight(false)} disabled={busy}>
                {t('common.actions.cancel')}
              </button>
            </div>
          </div>
        )}
        {error && (
          <p className="error-message" role="alert" data-testid="edit-song-error">{error}</p>
        )}
        <div className="button-group">
          {loading || busy ? (
            <LoadingSpinner />
          ) : (
            <button type="submit" className="btn-save">{t('common.actions.save')}</button>
          )}
          <button type="button" onClick={onClose} className="btn-cancel">{t('common.actions.cancel')}</button>
        </div>
      </form>
    </Modal>
  );
};

export default EditSongModal;
